import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Lock, Mail, Phone, User, Grid3x3, Sun, Home, Compass, ArrowRight, Shield } from 'lucide-react';
import axios from 'axios';
import { trackAddToCart, trackViewContent } from '../utils/metaPixel';

const gold = '#C9A961';
const surface = '#111633';

const plans = {
  numerology: { id: 'numerology', name: 'Numerology', icon: <Grid3x3 className="w-6 h-6" />, defaultPrice: 599, features: ['Grid Analysis', 'Name Correction', 'Business Name Check', 'Love Compatibility', 'Lucky Numbers & Dates', 'Professional Report'] },
  astrology: { id: 'astrology', name: 'Astrology', icon: <Sun className="w-6 h-6" />, defaultPrice: 599, features: ['Kundali Chart', 'Planetary Transits', 'Dasha Predictions', 'Dosha Detection', 'Life Predictions', 'Remedial Solutions'] },
  vastu: { id: 'vastu', name: 'Vastu', icon: <Home className="w-6 h-6" />, defaultPrice: 599, features: ['Room Placement Analysis', 'Directional Scoring', 'Defect Detection', 'Personalized Remedies', 'KUA Integration', 'Zone Reports'] },
  weekly_report: { id: 'weekly_report', name: 'Weekly Intelligence', icon: <Compass className="w-6 h-6" />, defaultPrice: 599, features: ['Daily Energy Snapshots', 'Life Area Scoring', 'Planetary Stress Alerts', 'DO/DONT Guidance', 'Timing Optimization', 'Weekly Briefs'] },
  bundle: { id: 'bundle', name: 'All-in-One Bundle', icon: <Shield className="w-6 h-6" />, defaultPrice: 1499, features: ['All Numerology Features', 'All Astrology Features', 'All Vastu Features', 'Weekly Intelligence', 'Daily Energy Dashboard', 'Classified Remedies', 'Lifetime Access'] },
};

const CheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan') || 'bundle';
  const navigate = useNavigate();

  const [selectedPlan, setSelectedPlan] = useState(planId);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [processing, setProcessing] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState(null);
  const [dynamicPricing, setDynamicPricing] = useState(null);

  useEffect(() => {
    axios.get('/api/pricing').then(r => { if (r.data?.plans) setDynamicPricing(r.data.plans); }).catch(() => {});
    trackViewContent('checkout_page');
  }, []);

  const getPrice = (id) => {
    if (dynamicPricing?.[id]) return dynamicPricing[id].display_price;
    return plans[id]?.defaultPrice || 1499;
  };

  const getDiscountedPrice = (price) => {
    if (!couponResult?.valid) return price;
    if (couponResult.discount_type === 'percentage') return Math.round(price * (1 - couponResult.discount_value / 100));
    return Math.max(0, price - couponResult.discount_value);
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const res = await axios.post('/api/coupons/validate', { code: couponCode, plan_id: selectedPlan });
      setCouponResult(res.data);
    } catch (e) {
      setCouponResult(null);
      alert(e.response?.data?.detail || 'Invalid coupon');
    }
  };

  const currentPlan = plans[selectedPlan] || plans.bundle;
  const basePrice = getPrice(selectedPlan);
  const finalPrice = getDiscountedPrice(basePrice);
  const isBundle = selectedPlan === 'bundle';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.phone || !formData.name) { alert('Please fill all fields'); return; }
    if (!formData.email.includes('@')) { alert('Please enter a valid email'); return; }
    if (formData.phone.length !== 10) { alert('Please enter a valid 10-digit phone number'); return; }

    setProcessing(true);
    try {
      trackAddToCart(currentPlan.name, finalPrice);
      const response = await axios.post('/api/payments/guest-checkout', {
        plan_id: currentPlan.id, plan_name: currentPlan.name,
        amount: finalPrice * 100, user_email: formData.email,
        user_phone: formData.phone, user_name: formData.name,
        coupon_code: couponResult?.valid ? couponCode : undefined,
      });
      if (response.data.success && response.data.redirect_url) {
        localStorage.setItem('pending_order_id', response.data.merchant_order_id);
        window.location.href = response.data.redirect_url;
      } else { alert(response.data.message || 'Payment failed'); setProcessing(false); }
    } catch (error) { alert(error.response?.data?.detail || 'Error initiating payment'); setProcessing(false); }
  };

  return (
    <div className="min-h-screen py-8 px-4 md:px-8" style={{ background: '#0A0F2A', fontFamily: 'Manrope, sans-serif' }}>
      <div className="max-w-5xl mx-auto">
        {/* Back */}
        <button onClick={() => navigate('/')} className="text-slate-500 hover:text-white transition-colors flex items-center gap-2 mb-8 text-sm font-medium" data-testid="checkout-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </button>

        <div className="grid lg:grid-cols-5 gap-8">

          {/* LEFT — Order Summary (3 cols) */}
          <div className="lg:col-span-3">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-8 tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Complete Your Order</h1>

            {/* Plan Selector */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 mb-6">
              {Object.values(plans).map(p => (
                <button key={p.id} onClick={() => { setSelectedPlan(p.id); setCouponResult(null); }}
                  className={`p-3 rounded-xl border text-left transition-all text-sm ${selectedPlan === p.id ? '' : 'opacity-60 hover:opacity-80'}`}
                  style={{ borderColor: selectedPlan === p.id ? gold : 'rgba(255,255,255,0.06)', background: selectedPlan === p.id ? 'rgba(201,169,97,0.08)' : 'rgba(255,255,255,0.02)' }}
                  data-testid={`checkout-plan-${p.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span style={{ color: selectedPlan === p.id ? gold : '#94A3B8' }}>{p.icon}</span>
                    <span className="text-white font-semibold text-xs">{p.name}</span>
                  </div>
                  <span className="font-bold text-white">&#8377;{getPrice(p.id)}</span>
                </button>
              ))}
            </div>

            {/* Selected Plan Details */}
            <div className="rounded-2xl border p-6 mb-6" style={{ borderColor: 'rgba(201,169,97,0.2)', background: 'rgba(201,169,97,0.03)' }}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(201,169,97,0.12)', color: gold }}>{currentPlan.icon}</div>
                <div>
                  <h2 className="text-xl font-bold text-white">{currentPlan.name}</h2>
                  <p className="text-slate-500 text-xs">Lifetime access. One-time purchase.</p>
                </div>
              </div>
              <ul className="grid grid-cols-2 gap-2.5">
                {currentPlan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-400"><Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: gold }} />{f}</li>
                ))}
              </ul>
            </div>

            {/* Bundle Upsell */}
            {!isBundle && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl border p-5" style={{ borderColor: 'rgba(201,169,97,0.3)', background: 'rgba(201,169,97,0.05)' }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-white font-bold text-sm">Upgrade to All-in-One Bundle</h3>
                    <p className="text-slate-400 text-xs">Get all 4 modules for just &#8377;{getPrice('bundle') - getPrice(selectedPlan)} more</p>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-lg" style={{ color: gold }}>&#8377;{getPrice('bundle')}</span>
                    <span className="text-slate-500 text-xs block">lifetime</span>
                  </div>
                </div>
                <button onClick={() => { setSelectedPlan('bundle'); setCouponResult(null); }}
                  className="w-full py-2.5 rounded-xl font-semibold text-sm transition-all hover:opacity-90"
                  style={{ background: gold, color: '#0A0F2A' }}
                  data-testid="checkout-upgrade-bundle"
                >
                  Upgrade to Bundle
                </button>
              </motion.div>
            )}
          </div>

          {/* RIGHT — Payment Form (2 cols) */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl border p-6 sticky top-8" style={{ borderColor: 'rgba(255,255,255,0.08)', background: surface }}>
              <h2 className="text-lg font-bold text-white mb-5">Your Details</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                {[
                  { label: 'Full Name', icon: <User className="w-4 h-4" />, type: 'text', key: 'name', placeholder: 'Enter your full name' },
                  { label: 'Email Address', icon: <Mail className="w-4 h-4" />, type: 'email', key: 'email', placeholder: 'your@email.com' },
                  { label: 'Phone Number', icon: <Phone className="w-4 h-4" />, type: 'tel', key: 'phone', placeholder: '9876543210' },
                ].map(field => (
                  <div key={field.key}>
                    <label className="text-slate-400 text-xs font-semibold mb-1.5 block">{field.label}</label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500">{field.icon}</span>
                      <input type={field.type} value={formData[field.key]}
                        onChange={(e) => setFormData({...formData, [field.key]: field.key === 'phone' ? e.target.value.replace(/\D/g, '').slice(0, 10) : e.target.value })}
                        placeholder={field.placeholder} required maxLength={field.key === 'phone' ? 10 : undefined}
                        className="w-full rounded-xl px-10 py-3 text-sm text-white placeholder-slate-600 focus:outline-none transition-colors"
                        style={{ background: '#0A0F2A', border: '1px solid rgba(255,255,255,0.08)' }}
                        data-testid={`checkout-${field.key}-input`}
                      />
                    </div>
                  </div>
                ))}

                {/* Coupon */}
                <div>
                  <label className="text-slate-400 text-xs font-semibold mb-1.5 block">Coupon Code (optional)</label>
                  <div className="flex gap-2">
                    <input type="text" value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="ENTER CODE" className="flex-1 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-600 focus:outline-none uppercase"
                      style={{ background: '#0A0F2A', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="checkout-coupon-input" />
                    <button type="button" onClick={applyCoupon} className="px-4 py-3 rounded-xl text-xs font-semibold border transition-all hover:bg-[rgba(201,169,97,0.1)]"
                      style={{ borderColor: 'rgba(201,169,97,0.3)', color: gold }} data-testid="checkout-apply-coupon">Apply</button>
                  </div>
                  {couponResult?.valid && <p className="text-xs mt-1.5" style={{ color: '#10B981' }}>{couponResult.message}</p>}
                </div>

                {/* Order Summary */}
                <div className="rounded-xl p-4 border" style={{ background: '#0A0F2A', borderColor: 'rgba(255,255,255,0.06)' }}>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-400">{currentPlan.name}</span>
                    <span className={`text-white ${couponResult?.valid ? 'line-through text-slate-500' : 'font-semibold'}`}>&#8377;{basePrice}</span>
                  </div>
                  {couponResult?.valid && (
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-green-400">Discount</span>
                      <span className="text-green-400">-&#8377;{basePrice - finalPrice}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                    <span className="text-white font-bold">Total</span>
                    <span className="font-bold text-xl" style={{ color: finalPrice === 0 ? '#10B981' : gold }}>{finalPrice === 0 ? 'FREE' : `₹${finalPrice}`}</span>
                  </div>
                  <p className="text-slate-600 text-xs mt-1 text-right">{finalPrice === 0 ? '100% discount applied' : 'One-time payment'}</p>
                </div>

                <button type="submit" disabled={processing}
                  className="w-full py-4 rounded-xl font-bold text-base transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ background: gold, color: '#0A0F2A' }} data-testid="proceed-to-payment">
                  {processing ? 'Processing...' : finalPrice === 0 ? <>Activate Free Access <ArrowRight className="w-5 h-5" /></> : <>Pay &#8377;{finalPrice} <ArrowRight className="w-5 h-5" /></>}
                </button>

                <div className="flex items-center justify-center gap-2 text-slate-500 text-xs">
                  <Lock className="w-3.5 h-3.5" />
                  <span>{finalPrice === 0 ? 'Coupon applied — no payment needed' : 'Secure checkout powered by PhonePe'}</span>
                </div>
              </form>

              <p className="text-xs text-slate-600 mt-5 text-center">
                By proceeding, you agree to our <a href="/terms" className="hover:underline" style={{ color: gold }}>Terms</a> and <a href="/privacy" className="hover:underline" style={{ color: gold }}>Privacy Policy</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
